import React, { useState } from 'react';
import { MessageCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { useTranslation } from '../i18n';
import apiClient from '../utils/api';
import { openWhatsApp } from '../services/whatsapp';

const Onboarding = ({ onComplete }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(2);
  const [selectedPlatform] = useState('whatsapp');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');

  const startWhatsAppConversation = async () => {
    setIsVerifying(true);
    setError('');

    try {
      // Get bot phone number from config
      const configResponse = await apiClient.get('/config');
      const botPhone = configResponse.data?.whatsapp?.botPhoneNumber || '5519994823795';
      
      // Generate verification code
      const response = await apiClient.post('/whatsapp/generate-verification', {
        phone_number: `+${botPhone}`
      });
      
      const code = response.data.code;
      const message = t('whatsappVerificationMessage').replace('{code}', code);
      
      // Open WhatsApp with verification message
      openWhatsApp(botPhone, message);
      
      // Move to success step
      setStep(3);
    } catch (err) {
      setError('Failed to generate verification code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSkipSetup = async () => {
    try {
      await apiClient.post('/user/skip-setup');
      
      // Check if user selected a plan and create trial
      const selectedPlan = localStorage.getItem('selectedPlan');
      if (selectedPlan) {
        try {
          const plan = JSON.parse(selectedPlan);
          await apiClient.post('/subscription/create-trial', plan);
          localStorage.removeItem('selectedPlan');
        } catch (err) {
          console.error('Failed to create trial:', err);
        }
      }
      
      onComplete();
    } catch (error) {
      console.error('Failed to skip setup:', error);
      onComplete();
    }
  };

  const renderStep = () => {
    switch (step) {
      case 2:
        return (
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: '#25D366',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <MessageCircle size={30} color="white" />
            </div>
            
            <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '1rem' }}>
              {t('connectWhatsApp')}
            </h2>
            <p style={{ color: '#666', marginBottom: '2rem' }}>
              {t('clickStartConversationToVerify')}
            </p>
            
            {error && (
              <div style={{ color: '#dc3545', fontSize: '0.875rem', marginBottom: '1rem' }}>
                {error}
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={handleSkipSetup}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: 'white',
                  color: '#666',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {t('setupLater')}
              </button>
              <button
                onClick={startWhatsAppConversation}
                disabled={isVerifying}
                style={{
                  flex: 2,
                  padding: '0.75rem',
                  backgroundColor: isVerifying ? '#e0e0e0' : '#25D366',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: isVerifying ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                💬 {isVerifying ? t('opening') : t('startConversation')}
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              backgroundColor: '#25D366',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <MessageCircle size={30} color="white" />
            </div>
            
            <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '1rem' }}>
              {t('conversationStarted')}
            </h2>
            <p style={{ color: '#666', marginBottom: '2rem' }}>
              {t('sendVerificationCodeInWhatsApp')}
            </p>
            
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '1rem', 
              borderRadius: '8px', 
              marginBottom: '2rem',
              textAlign: 'left'
            }}>
              <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
                {t('nextSteps')}:
              </p>
              <ol style={{ fontSize: '0.875rem', color: '#666', paddingLeft: '1.5rem', margin: 0 }}>
                <li>{t('sendTheVerificationCode')}</li>
                <li>{t('aiWillConfirmAndSetup')}</li>
                <li>{t('startUsingSmartGTD')}</li>
              </ol>
            </div>
            
            <button
              onClick={async () => {
                // Check if user selected a plan and create trial
                const selectedPlan = localStorage.getItem('selectedPlan');
                if (selectedPlan) {
                  try {
                    const plan = JSON.parse(selectedPlan);
                    await apiClient.post('/subscription/create-trial', plan);
                    localStorage.removeItem('selectedPlan');
                  } catch (err) {
                    console.error('Failed to create trial:', err);
                  }
                }
                onComplete();
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {t('goToDashboard')} <ArrowRight size={16} />
            </button>
          </div>
        );

      case 4:
        return (
          <div style={{ textAlign: 'center', maxWidth: '400px' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#28a745',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              <CheckCircle size={40} color="white" />
            </div>
            
            <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '1rem' }}>
              {t('allSet')}
            </h2>
            <p style={{ color: '#666', marginBottom: '2rem' }}>
              {t('platformConnected').replace('{platform}', 'WhatsApp')}
            </p>
            
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '1rem',
              borderRadius: '8px',
              fontSize: '0.875rem',
              color: '#666'
            }}>
              <strong>{t('nextSteps')}</strong><br />
              {t('nextStepsMessage')}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '2rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        width: '100%',
        maxWidth: '500px'
      }}>
        {/* Progress indicator */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem'
          }}>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: i <= step ? '#007bff' : '#e0e0e0',
                  transition: 'background-color 0.3s'
                }}
              />
            ))}
          </div>
          <div style={{
            height: '2px',
            backgroundColor: '#e0e0e0',
            borderRadius: '1px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              backgroundColor: '#007bff',
              width: `${(step / 4) * 100}%`,
              transition: 'width 0.3s'
            }} />
          </div>
        </div>

        {renderStep()}
      </div>
    </div>
  );
};

export default Onboarding;